import type { PropsWithChildren, ReactNode } from 'react';

import { hasBaseUtility, hasBaseUtilityPrefix } from '../utils';

interface SkyLabelWrapperProps extends PropsWithChildren<{}> {
  className?: string;
  icon?: ReactNode;
}

/**
 * Tailwind `display` utilities. Tailwind v4 emits utilities in alphabetical order,
 * so `.inline-flex` lands after `.block`/`.contents`/`.flex`/`.hidden` in the
 * stylesheet and silently wins over any of them. Defaulting to `inline-flex`
 * unconditionally therefore made `<SkyLabel className="hidden" />` a no-op, which
 * is why callers had to wrap the label in an extra `<span>` (SKY-3715).
 */
const DISPLAY_UTILITIES: ReadonlySet<string> = new Set([
  'block',
  'contents',
  'flex',
  'flow-root',
  'grid',
  'hidden',
  'inline',
  'inline-block',
  'inline-flex',
  'inline-grid',
  'inline-table',
  'list-item',
  'table',
  'table-caption',
  'table-cell',
  'table-column',
  'table-column-group',
  'table-footer-group',
  'table-header-group',
  'table-row',
  'table-row-group',
]);

/**
 * Tailwind `font-weight` utilities. Deliberately not every `font-*` utility:
 * `font-mono`/`font-sans` set the family and must not suppress the default weight.
 */
const FONT_WEIGHT_UTILITIES: ReadonlySet<string> = new Set([
  'font-black',
  'font-bold',
  'font-extrabold',
  'font-extralight',
  'font-light',
  'font-medium',
  'font-normal',
  'font-semibold',
  'font-thin',
]);

/** Tailwind `text-transform` utilities. */
const TEXT_TRANSFORM_UTILITIES: ReadonlySet<string> = new Set([
  'capitalize',
  'lowercase',
  'normal-case',
  'uppercase',
]);

/**
 * Wrapper component for SkyLabel to apply consistent styling
 * For internal use only
 *
 * Each default is skipped when `className` already sets a base-level utility from
 * the same family, so callers can override `display`, font weight, text transform
 * and letter spacing without fighting CSS source order.
 */
export const SkyLabelWrapper = ({
  children,
  className = '',
  icon = undefined,
}: SkyLabelWrapperProps) => {
  const cls = [
    hasBaseUtility(className, DISPLAY_UTILITIES) ? '' : 'inline-flex',
    'items-baseline',
    'gap-2',
    'max-w-full',
    hasBaseUtility(className, FONT_WEIGHT_UTILITIES) ? '' : 'font-semibold',
    hasBaseUtility(className, TEXT_TRANSFORM_UTILITIES) ? '' : 'uppercase',
    hasBaseUtilityPrefix(className, 'tracking-') ? '' : 'tracking-wider',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Note: unfortunately we can't use Space because of antd issue
  // https://github.com/ant-design/ant-design/issues/55693
  return (
    <span className={cls} data-testid="sky-label-span">
      {icon ? <span>{icon}</span> : null}
      {children}
    </span>
  );
};
